import { useQueries, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { fetchAppointments } from "../../api/appointments";
import { fetchFollowups } from "../../api/followups";
import { listTasks, type TaskItem } from "../../api/tasks";
import { useServices } from "../../app/services";
import { addDays } from "../../lib/dates";
import { useHomeScope, type Scope } from "../home/hooks";

export type { Scope };
export const useScope = useHomeScope;

/** `?date=` when it is a calendar date, else today. */
export function useDateParam(sc: Scope) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("date");
  const date = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : sc.date;
  const setDate = (d: string) => {
    const next = new URLSearchParams(params);
    if (d === sc.date) next.delete("date"); else next.set("date", d);
    setParams(next, { replace: true });
  };
  return { date, setDate, params, setParams };
}

export function useFollowupList(sc: Scope, date: string, ownerStaffId?: string) {
  const { client } = useServices();
  return useQuery({
    queryKey: ["followups", ...sc.scopeKey, date, ownerStaffId ?? "all"],
    queryFn: () => fetchFollowups(client, { date, branchId: sc.branchId, ownerStaffId }),
    retry: false,
  });
}

/** Monday of the week holding `ymd`: pure calendar arithmetic on the date string. */
export function weekStart(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(ymd, -((dow + 6) % 7));
}

export function useAppointmentDays(sc: Scope, dates: string[]) {
  const { client } = useServices();
  return useQueries({
    queries: dates.map((d) => ({
      queryKey: ["appointments", ...sc.scopeKey, d],
      queryFn: () => fetchAppointments(client, { date: d, branchId: sc.branchId }),
      retry: false,
    })),
  });
}

export type TaskScope = "mine" | "team" | "done";

/** Open tasks of one or many assignees, or my done tasks. The API takes one assignee per call, so a team view fans out. */
export function useTaskLists(sc: Scope, scope: TaskScope, assignees: string[]) {
  const { client } = useServices();
  const who = scope === "team" ? assignees : ["me"];
  const status = scope === "done" ? "done" : "open";
  const results = useQueries({
    queries: who.map((a) => ({
      queryKey: ["tasks", ...sc.scopeKey, scope, a],
      queryFn: () => listTasks(client, { assignee: a, status }),
      retry: false,
    })),
  });
  const pending = results.some((r) => r.isPending);
  const error = results.find((r) => r.error)?.error ?? null;
  const tasks: TaskItem[] = results.flatMap((r) => r.data?.tasks ?? []);
  const truncated = results.some((r) => r.data?.truncated);
  return { results, pending, error, tasks, truncated, refetch: () => results.forEach((r) => void r.refetch()) };
}
